<template>
    <AppLayout>
        <div class="container p-4 mx-auto md:px-1 md:py-4">
            <!-- Page Title -->
            <div class="mb-6">
                <h1 class="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                    {{ $tc('LBL_LOCATION_LOG', 2) }}
                </h1>

                <!-- Breadcrumb -->
                <nav
                    class="text-sm text-gray-500 mb-6"
                    aria-label="Breadcrumb"
                >
                    <ol class="flex items-center space-x-2">
                        <li class="flex items-center">
                            <inertia-link
                                :href="route('landing')"
                                class="text-green-600 hover:underline"
                            >
                                {{ $t('LBL_HOME') }}
                            </inertia-link>
                            <svg
                                class="w-3 h-3 mx-2 text-green-400"
                                fill="currentColor"
                                viewBox="0 0 320 512"
                            >
                                <path
                                    d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z"
                                />
                            </svg>
                        </li>
                        <li class="text-gray-700">
                            {{ $tc('LBL_USER', 1) }}
                        </li>
                    </ol>
                </nav>

                <!-- FILTER BAR (only for wholesaler) -->
                <!-- <div
                    v-if="isWholesaler"
                    class="bg-white rounded shadow p-4 mb-4"
                >
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                        <div>
                            <label class="block text-xs text-gray-500 mb-1">Salesperson</label>
                            <select
                                v-model="form.salesperson_id"
                                class="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                            >
                                <option :value="''">All salespeople</option>
                                <option
                                    v-for="sp in salespeople"
                                    :key="sp.id"
                                    :value="sp.id"
                                >
                                    {{ sp.name }}
                                </option>
                            </select>
                        </div>

                        <div>
                            <label class="block text-xs text-gray-500 mb-1">Search (retailer/salesperson)</label>
                            <input
                                v-model="form.q"
                                type="text"
                                class="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                                placeholder="e.g. ABC Retailer or John"
                                @keydown.enter="applyFilters"
                            />
                        </div>

                        <div class="flex gap-2">
                            <button
                                class="px-4 py-2 text-sm font-semibold text-white bg-gray-800 rounded hover:bg-gray-700"
                                @click="applyFilters"
                            >
                                Search
                            </button>

                            <button
                                class="px-4 py-2 text-sm font-semibold border rounded hover:bg-gray-50"
                                @click="resetFilters"
                            >
                                Reset
                            </button>
                        </div>
                    </div>
                </div> -->

                <div
                    class="w-full bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-overflow"
                >
                    <!-- Search & Action Bar -->
                    <div
                        v-if="isWholesaler"
                        class="flex flex-wrap items-center justify-between gap-4 p-4 sm:flex-nowrap"
                    >
                        <!-- Search Box -->
                        <div class="relative w-full sm:w-auto">
                            <label
                                for="table-search"
                                class="sr-only"
                                >Search</label
                            >
                            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg
                                    class="w-5 h-5 text-gray-500 dark:text-gray-400"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                        clip-rule="evenodd"
                                    />
                                </svg>
                            </div>
                            <t-input
                                id="salesperson-search-box"
                                v-model="search"
                                type="text"
                                variant="success"
                                class="w-full sm:w-80 pl-10 pr-4 py-2.5 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:outline-none dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                                placeholder="Search for salesperson"
                                @keypress.enter="fetchPageData()"
                            />
                        </div>

                        <!-- Action Buttons -->
                        <div class="flex flex-wrap gap-2">
                            <t-rich-select
                                v-model="salesperson_id"
                                class="w-60 text-sm"
                                :clearable="true"
                                :close-on-select="false"
                                :options="salespeople"
                                value-attribute="id"
                                text-attribute="name"
                                placeholder="Filter by salesperson"
                            />
                            <t-button
                                class="inline-flex items-center rounded-md"
                                variant="success"
                                @click="fetchPageData()"
                            >
                                <span>Search</span>
                            </t-button>

                            <t-button
                                class="inline-flex items-center rounded-md"
                                variant="success"
                                @click="resetFilters"
                            >
                                <span>Clear</span>
                            </t-button>
                        </div>
                    </div>

                    <div
                        class="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-none shadow-md"
                    >
                        <div class="overflow-x-auto">
                            <table
                                class="min-w-[700px] w-full table-auto text-sm sm:text-base text-left text-gray-700 dark:text-gray-300"
                            >
                                <thead class="bg-gray-100 dark:bg-gray-700 text-sm font-semibold">
                                    <tr>
                                        <th class="text-left p-3">RETAILER</th>
                                        <th class="text-left p-3">CLOCK IN</th>
                                        <th class="text-left p-3">CLOCK OUT</th>
                                        <th class="text-left p-3">MAP IN</th>
                                        <th class="text-left p-3">MAP OUT</th>
                                        <th class="text-left p-3">DETAILS</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    <tr
                                        v-for="row in logs.data"
                                        :key="row.id"
                                        class="border-b"
                                    >
                                        <td class="p-3">
                                            {{ row.retailer && row.retailer.name ? row.retailer.name : '-' }}
                                        </td>

                                        <td class="p-3">
                                            {{ row.clock_in_at ? row.clock_in_at : '-' }}
                                        </td>

                                        <td class="p-3">
                                            <span v-if="row.clock_out_at">{{ row.clock_out_at }}</span>
                                            <span
                                                v-else
                                                class="font-semibold text-orange-600"
                                                >OPEN</span
                                            >
                                        </td>

                                        <td class="p-3">
                                            <a
                                                v-if="mapLink(row.latitude, row.longitude)"
                                                :href="mapLink(row.latitude, row.longitude)"
                                                target="_blank"
                                                rel="noopener"
                                                class="text-blue-600 hover:underline"
                                            >
                                                Open
                                            </a>
                                            <span v-else>-</span>
                                        </td>

                                        <td class="p-3">
                                            <a
                                                v-if="mapLink(row.clock_out_latitude, row.clock_out_longitude)"
                                                :href="mapLink(row.clock_out_latitude, row.clock_out_longitude)"
                                                target="_blank"
                                                rel="noopener"
                                                class="text-blue-600 hover:underline"
                                            >
                                                Open
                                            </a>
                                            <span v-else>-</span>
                                        </td>

                                        <td class="p-3">
                                            <button
                                                class="px-3 py-1 text-xs font-semibold text-white bg-green-600 rounded hover:bg-green-700"
                                                @click="openDetailMap(row)"
                                            >
                                                View
                                            </button>
                                        </td>
                                    </tr>

                                    <tr
                                        v-if="!logs.data || !logs.data.length"
                                        class="text-center"
                                    >
                                        <td
                                            colspan="6"
                                            class="px-4 py-4 text-gray-500 dark:text-gray-400"
                                        >
                                            {{ $t('LBL_DATA_FOUND') }}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div
                            v-if="logs.data.links && logs.data.links.length"
                            class="flex justify-center items-center bg-gray-100 dark:bg-gray-800 px-4 py-4 border-t dark:border-gray-700"
                        >
                            <t-pagination
                                v-model="currentPage"
                                variant="rounded"
                                :total-items="totalRows"
                                :per-page="perPage"
                                :limit="limit"
                                :disabled="!logs.data.links || !logs.data.links.length"
                                @change="fetchPageData"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </AppLayout>
</template>

<script>
    import AppLayout from '../../Layouts/AppLayout.vue';
    export default {
        name: 'LocationLogsIndex',

        components: {
            AppLayout,
        },

        props: {
            logs: {
                type: Object,
                required: true,
            },
            appliedFilters: {
                type: Object,
                default: () => ({}),
            },
            salespeople: {
                type: Array,
                default: () => [],
            },

            // appliedFilters: {
            //     type: Object,
            //     required: true,
            // },
        },

        data() {
            return {
                // form: {
                //     salesperson_id: this.filters.salesperson_id || '',
                //     q: this.filters.q || '',
                // },

                salesperson_id: this.appliedFilters.salesperson_id || null,

                currentPage: null,
                limit: 5,
                perPage: this.logs.per_page,
                totalRows: this.logs.total,
                search: null,
            };
        },

        methods: {
            mapLink(lat, lng) {
                if (!lat || !lng) return null;
                // No API key required:
                return `https://www.google.com/maps?q=${lat},${lng}`;
                // alternative:
                // return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
            },

            openDetailMap(row) {
                // Prefer clock-out location if available, otherwise clock-in location
                const outLat = row.clock_out_latitude;
                const outLng = row.clock_out_longitude;

                const inLat = row.latitude;
                const inLng = row.longitude;

                const lat = outLat && outLng ? outLat : inLat;
                const lng = outLat && outLng ? outLng : inLng;

                const url = this.mapLink(lat, lng);
                if (!url) return;

                window.open(url, '_blank', 'noopener');
            },

            fetchPageData(newPage) {
                this.$inertia.get(
                    route('location-logs.index'),
                    {
                        page: newPage,
                        salesperson_id: this.salesperson_id || null,
                        keyword: this.search,
                    },
                    { preserveState: true, preserveScroll: true },
                );
            },

            // applyFilters() {
            //     // wholesaler only; salesperson page will ignore these anyway
            //     this.$inertia.get(
            //         route('location-logs.index'),
            //         {
            //             salesperson_id: this.form.salesperson_id || null,
            //             q: this.form.q || null,
            //         },
            //         { preserveState: true, preserveScroll: true },
            //     );
            // },

            resetFilters() {
                this.salesperson_id = null;
                this.search = null;
                this.fetchPageData(1);
            },
        },
    };
</script>
